import {render as toPlainText} from 'datocms-structured-text-to-plain-text'
import {
  type Heading,
  type Link,
  type List,
  type Node,
  type Paragraph,
  isListItem,
} from 'datocms-structured-text-utils'
import {kebabCase, some} from 'lodash'
import {type ReactNode, Fragment} from 'react'
import FEMLink from '~/components/FEMLink'
import {type HgWYSIWYGType} from '~/design-system/hg/components/HgWYSIWYG'
import {
  type BodyTextProps,
  HgWYSIWYGBodyText,
} from '~/design-system/hg/components/HgWYSIWYG/_components/BodyText'
import {HgWYSIWYGHeading} from '~/design-system/hg/components/HgWYSIWYG/_components/Heading'
import {HgWYSIWYGOrderedList} from '~/design-system/hg/components/HgWYSIWYG/_components/OrderedList'
import {HgWYSIWYGUnorderedList} from '~/design-system/hg/components/HgWYSIWYG/_components/UnorderedList'
import {safeUnreachableCase} from '~/utils/unreachableCase'
import {type PermittedHeadingLevel, wysiwygComponentVariants} from './variants'

type PermittedNestingLevel = 0 | 1 | 2

export const formatHeadingLink = (heading: Node) => {
  const plainText = toPlainText(heading) ?? ''
  const id = kebabCase(plainText ?? '')
  return {id, title: plainText}
}

export const handleHeadingNode = ({
  children,
  node,
  key,
  id,
}: {
  children: ReactNode
  node: Heading
  key: string
  id: string
}) => {
  const getTag = (level: PermittedHeadingLevel) => {
    switch (level) {
      case 2:
        return 'h2'
      case 3:
        return 'h3'
      case 4:
        return 'h4'
      default:
        return safeUnreachableCase(level, 'h4')
    }
  }
  const {heading} = wysiwygComponentVariants({
    headingLevel: node.level as PermittedHeadingLevel,
  })

  return (
    <HgWYSIWYGHeading
      key={key}
      tag={getTag(node.level as PermittedHeadingLevel)}
      id={id}
      className={heading()}
    >
      {children}
    </HgWYSIWYGHeading>
  )
}

export const handleLinkNode = ({
  children,
  node,
  key,
}: {
  children: ReactNode
  node: Link
  key: string
}) => {
  const newTab = some(node.meta, {id: 'target', value: '_blank'})
  return (
    <FEMLink key={key} href={node.url} variant="underline" newTab={newTab}>
      {children}
    </FEMLink>
  )
}

export const handleListNode = ({
  node,
  children,
  key,
  ancestors,
  wysiwygType,
}: {
  children: ReactNode
  node: List
  key: string
  ancestors: Node[]
  wysiwygType: HgWYSIWYGType
}) => {
  const numberOfNestedLists = ancestors.filter(isListItem).length
  const nestedLevel =
    numberOfNestedLists > 2 ? numberOfNestedLists % 3 : numberOfNestedLists
  const {list} = wysiwygComponentVariants()

  if (node.style === 'numbered') {
    return (
      <HgWYSIWYGOrderedList
        key={key}
        nestedLevel={nestedLevel as PermittedNestingLevel}
        className={list()}
        wysiwygType={wysiwygType}
      >
        {children}
      </HgWYSIWYGOrderedList>
    )
  }

  return (
    <HgWYSIWYGUnorderedList
      key={key}
      nestedLevel={nestedLevel as PermittedNestingLevel}
      className={list()}
      wysiwygType={wysiwygType}
    >
      {children}
    </HgWYSIWYGUnorderedList>
  )
}

export const handleListItemNode = ({
  children,
  key,
}: {
  children: ReactNode
  key: string
}) => {
  return <li key={key}>{children}</li>
}

export const handleParagraphNode = ({
  children,
  node,
  key,
  ancestors,
  wysiwygType,
}: {
  children: ReactNode
  node: Paragraph
  key: string
  ancestors: Node[]
  wysiwygType: HgWYSIWYGType
}) => {
  if (isListItem(ancestors[0])) {
    return <Fragment key={key}>{children}</Fragment>
  }

  const getBodyTextVariant = (variant: BodyTextProps['variant']) => {
    switch (variant) {
      case 'body-1':
        return 'body-1'
      case undefined:
      case 'body-2':
        return 'body-2'
      case 'body-3':
        return 'body-3'
      default:
        return safeUnreachableCase(variant, 'body-2')
    }
  }

  const variant = getBodyTextVariant(node.style as BodyTextProps['variant'])
  const {body} = wysiwygComponentVariants({bodyVariant: variant})

  return (
    <HgWYSIWYGBodyText
      className={body()}
      key={key}
      variant={variant}
      wysiwygType={wysiwygType}
    >
      {children}
    </HgWYSIWYGBodyText>
  )
}
